import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  Box,
  Chip,
  Collapse,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import FolderRoundedIcon from '@mui/icons-material/FolderRounded'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded'
import { docsSections } from '../routes/_config'
import { getDocsIndex } from './docsSearchIndex'

const isDocsHost = () =>
  window.location.hostname === `docs.${process.env.ROOT_DOMAIN}` ||
  window.location.hostname.startsWith('docs.')

const getDocHref = (sectionId, pageId) => {
  const base = isDocsHost() ? '' : '/docs'
  return `${base}/${sectionId}/${pageId}`
}

const normalizeDocsPath = (pathname) => {
  if (pathname === '/docs') return '/'
  if (pathname.startsWith('/docs/')) return pathname.replace('/docs', '')
  return pathname
}

const isPageSelected = (normalizedPath, sectionId, pageId) =>
  normalizedPath === `/${sectionId}/${pageId}`

const EXPANDED_STORAGE_KEY = 'ambunetDocsSidebarExpanded'

const readStoredExpanded = () => {
  try {
    const parsed = JSON.parse(
      window.sessionStorage.getItem(EXPANDED_STORAGE_KEY),
    )
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch (e) {
    return {}
  }
}

// Tier 3: page link rows. Sentence case, regular weight, tinted + teal when
// active — visually lighter than section rows, heavier than group eyebrows.
const pageButtonSx = (selected, nested = false) => ({
  mb: 0.25,
  py: 0.55,
  pl: nested ? 1.25 : 1.75,
  pr: 1,
  borderRadius: 1.5,
  color: selected ? '#1E7F88' : '#425062',
  bgcolor: selected ? 'rgba(35,154,164,0.12)' : 'transparent',
  transition: 'background 0.15s ease, color 0.15s ease',
  '&:hover': {
    bgcolor: selected ? 'rgba(35,154,164,0.16)' : 'rgba(15,23,42,0.045)',
  },
  '&.Mui-selected': {
    bgcolor: 'rgba(35,154,164,0.12)',
  },
  '&.Mui-selected:hover': {
    bgcolor: 'rgba(35,154,164,0.16)',
  },
})

function PageButton({
  page,
  sectionId,
  selected,
  navigate,
  nested = false,
  onNavigate,
  selectedRef,
  secondary,
}) {
  return (
    <ListItemButton
      ref={selected ? selectedRef : undefined}
      selected={selected}
      onClick={() => {
        navigate(getDocHref(sectionId, page.id))
        onNavigate?.()
      }}
      sx={pageButtonSx(selected, nested)}
    >
      <ListItemText
        primary={page.title}
        secondary={secondary}
        primaryTypographyProps={{
          fontSize: 13,
          fontWeight: selected ? 650 : 450,
          lineHeight: 1.35,
          color: 'inherit',
        }}
        secondaryTypographyProps={
          secondary
            ? { fontSize: 11, color: '#8A94A6', lineHeight: 1.3, mt: 0.25 }
            : undefined
        }
        sx={{ my: 0 }}
      />
    </ListItemButton>
  )
}

export default function DocsSidebar({ mobile = false, onNavigate }) {
  const navigate = useNavigate()
  const location = useLocation()
  const normalizedPath = normalizeDocsPath(location.pathname)
  const activeSectionId = normalizedPath.split('/')[1] || null

  const [expanded, setExpanded] = useState(readStoredExpanded)
  const [filter, setFilter] = useState('')
  const selectedRef = useRef(null)

  // Auto-expand whichever section the current page lives in, even if the
  // user previously collapsed it, so navigation via search / prev-next /
  // shared links always reveals your location.
  useEffect(() => {
    if (!activeSectionId) return
    setExpanded((prev) =>
      prev[activeSectionId] === true
        ? prev
        : { ...prev, [activeSectionId]: true },
    )
  }, [activeSectionId])

  useEffect(() => {
    try {
      window.sessionStorage.setItem(
        EXPANDED_STORAGE_KEY,
        JSON.stringify(expanded),
      )
    } catch (e) {
      // Storage unavailable (private mode etc.) — expansion just won't persist
    }
  }, [expanded])

  useEffect(() => {
    selectedRef.current?.scrollIntoView({ block: 'nearest' })
  }, [normalizedPath])

  const toggleSection = (sectionId) =>
    setExpanded((prev) => {
      const open =
        prev[sectionId] !== undefined
          ? prev[sectionId]
          : sectionId === activeSectionId
      return { ...prev, [sectionId]: !open }
    })

  const isSectionOpen = (sectionId) =>
    expanded[sectionId] !== undefined
      ? expanded[sectionId]
      : sectionId === activeSectionId

  const filterQuery = filter.trim()
  const filterHits = useMemo(() => {
    if (filterQuery.length < 2) return null
    try {
      return getDocsIndex()
        .search(filterQuery, { prefix: true, fuzzy: 0.2 })
        .slice(0, 30)
    } catch (e) {
      return []
    }
  }, [filterQuery])

  const handleNavigate = () => {
    setFilter('')
    onNavigate?.()
  }

  return (
    <Paper
      component="nav"
      aria-label="Documentation"
      elevation={0}
      sx={{
        display: mobile ? 'flex' : { xs: 'none', lg: 'flex' },
        flexDirection: 'column',
        position: mobile ? 'relative' : 'sticky',
        top: mobile ? 'auto' : 20,
        maxHeight: mobile ? 'none' : 'calc(100vh - 40px)',
        overflow: mobile ? 'visible' : 'hidden',
        boxShadow: mobile ? 'none' : '0 16px 48px rgba(15,23,42,0.07)',
        borderRadius: mobile ? 2 : 3,
        border: '1px solid rgba(15,23,42,0.07)',
        bgcolor: '#FFFFFF',
      }}
    >
      <Box
        sx={{
          p: 1.5,
          pb: 1.25,
          borderBottom: '1px solid rgba(15,23,42,0.07)',
        }}
      >
        <Box sx={{ px: 0.5, mb: 1 }}>
          <Typography sx={{ fontSize: 12.5, color: '#64748B' }}>
            Browse by AmbuNet module
          </Typography>
        </Box>

        <TextField
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          size="small"
          fullWidth
          placeholder="Filter pages…"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRoundedIcon sx={{ fontSize: 18, color: '#94A3B8' }} />
              </InputAdornment>
            ),
            endAdornment: filter ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  aria-label="Clear filter"
                  onClick={() => setFilter('')}
                >
                  <CloseRoundedIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 1.5,
              bgcolor: '#F8FAFC',
              fontSize: 13,
            },
          }}
        />
      </Box>

      <Box sx={{ flex: 1, overflowY: mobile ? 'visible' : 'auto', p: 1.5 }}>
        {filterHits ? (
          <List dense disablePadding>
            {filterHits.length === 0 && (
              <Typography
                sx={{ px: 1, py: 2, fontSize: 13, color: '#64748B' }}
              >
                No pages match “{filterQuery}”.
              </Typography>
            )}
            {filterHits.map((hit) => (
              <PageButton
                key={hit.id}
                page={{ id: hit.pageId, title: hit.title }}
                sectionId={hit.sectionID}
                selected={isPageSelected(
                  normalizedPath,
                  hit.sectionID,
                  hit.pageId,
                )}
                navigate={navigate}
                onNavigate={handleNavigate}
                selectedRef={selectedRef}
                secondary={
                  hit.group ? `${hit.section} · ${hit.group}` : hit.section
                }
              />
            ))}
          </List>
        ) : (
          <Stack spacing={0.25}>
            {docsSections.map((section) => {
              const groups = Array.isArray(section.groups)
                ? section.groups
                : []
              const pages = Array.isArray(section.pages) ? section.pages : []
              const sectionActive = activeSectionId === section.id
              const open = isSectionOpen(section.id)

              return (
                <Box key={section.id}>
                  {/* Tier 1: section row — dark, bold, clickable, chevron */}
                  <ListItemButton
                    onClick={() => toggleSection(section.id)}
                    aria-expanded={open}
                    sx={{
                      borderRadius: 1.5,
                      px: 1,
                      py: 0.7,
                      bgcolor: sectionActive
                        ? 'rgba(35,154,164,0.06)'
                        : 'transparent',
                      '&:hover': { bgcolor: 'rgba(15,23,42,0.045)' },
                    }}
                  >
                    <FolderRoundedIcon
                      sx={{
                        fontSize: 17,
                        mr: 1,
                        color: sectionActive ? '#239AA4' : '#94A3B8',
                      }}
                    />
                    <ListItemText
                      primary={section.title}
                      primaryTypographyProps={{
                        fontSize: 13.5,
                        fontWeight: 700,
                        color: sectionActive ? '#1E7F88' : '#242F40',
                      }}
                      sx={{ my: 0 }}
                    />
                    <ExpandMoreRoundedIcon
                      sx={{
                        fontSize: 18,
                        color: '#94A3B8',
                        transform: open ? 'rotate(0deg)' : 'rotate(-90deg)',
                        transition: 'transform 0.18s ease',
                      }}
                    />
                  </ListItemButton>

                  <Collapse in={open} timeout={180} unmountOnExit>
                    <Box sx={{ pb: 0.75 }}>
                      {groups.length > 0 ? (
                        // Preserve route-file order: standalone pages and
                        // groups interleave exactly as authored, matching
                        // the prev/next footer order.
                        groups.map((group) =>
                          Array.isArray(group.pages) ? (
                            <Box key={group.id} sx={{ mt: 0.5 }}>
                              {/* Tier 2: group eyebrow — the only uppercase tier */}
                              <Typography
                                sx={{
                                  pl: 1.75,
                                  pb: 0.35,
                                  fontSize: 11,
                                  fontWeight: 700,
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.07em',
                                  color: '#8A94A6',
                                }}
                              >
                                {group.title}
                              </Typography>

                              <List
                                dense
                                disablePadding
                                sx={{
                                  ml: 1.9,
                                  pl: 0.5,
                                  borderLeft: group.pages.some((page) =>
                                    isPageSelected(
                                      normalizedPath,
                                      section.id,
                                      page.id,
                                    ),
                                  )
                                    ? '2px solid rgba(35,154,164,0.55)'
                                    : '2px solid rgba(15,23,42,0.08)',
                                }}
                              >
                                {group.pages.map((page) => (
                                  <PageButton
                                    key={page.id}
                                    page={page}
                                    sectionId={section.id}
                                    selected={isPageSelected(
                                      normalizedPath,
                                      section.id,
                                      page.id,
                                    )}
                                    navigate={navigate}
                                    nested
                                    onNavigate={handleNavigate}
                                    selectedRef={selectedRef}
                                  />
                                ))}
                              </List>
                            </Box>
                          ) : (
                            <PageButton
                              key={group.id}
                              page={group}
                              sectionId={section.id}
                              selected={isPageSelected(
                                normalizedPath,
                                section.id,
                                group.id,
                              )}
                              navigate={navigate}
                              onNavigate={handleNavigate}
                              selectedRef={selectedRef}
                            />
                          ),
                        )
                      ) : (
                        <List dense disablePadding sx={{ mt: 0.25 }}>
                          {pages.map((page) => (
                            <PageButton
                              key={page.id}
                              page={page}
                              sectionId={section.id}
                              selected={isPageSelected(
                                normalizedPath,
                                section.id,
                                page.id,
                              )}
                              navigate={navigate}
                              onNavigate={handleNavigate}
                              selectedRef={selectedRef}
                            />
                          ))}
                        </List>
                      )}
                    </Box>
                  </Collapse>
                </Box>
              )
            })}
          </Stack>
        )}

        <Box
          sx={{
            mt: 2,
            p: 1.5,
            borderRadius: 2,
            background:
              'linear-gradient(180deg, rgba(35,154,164,0.08) 0%, #F8FAFC 100%)',
            border: '1px solid rgba(15,23,42,0.06)',
          }}
        >
          <Chip
            icon={<HelpOutlineRoundedIcon />}
            label="Need help?"
            size="small"
            sx={{
              mb: 1,
              fontWeight: 800,
              color: '#239AA4',
              bgcolor: 'rgba(35,154,164,0.10)',
              '& .MuiChip-icon': {
                color: '#239AA4',
              },
            }}
          />

          <Typography variant="body2" color="text.secondary" lineHeight={1.6}>
            Can’t find what you need? Use the support portal from inside
            AmbuNet.
          </Typography>
        </Box>
      </Box>
    </Paper>
  )
}
